/**
 * Azure Entra User Picker Component
 * 
 * A searchable picker for selecting users from Azure Entra ID (Azure AD).
 * Used for managing admin and super admin users in the Admin Portal.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Stack,
    TextField,
    IconButton,
    Persona,
    PersonaSize,
    PersonaPresence,
    Text,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    DefaultButton,
    Callout,
    DirectionalHint,
    Label,
    mergeStyles,
    IPersonaProps,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { authenticatedFetch } from '../utils/authApi';

// Types
interface EntraUser {
    id: string;
    displayName: string;
    email: string;
    userPrincipalName: string;
    jobTitle?: string;
    department?: string;
    officeLocation?: string;
    notInEntra?: boolean;
    originalEmail?: string; // The original email from selectedEmails, used for removal
}

interface EntraUserPickerProps {
    /** Currently selected user emails */
    selectedEmails: string[];
    /** Callback when selection changes */
    onSelectionChange: (emails: string[]) => void;
    /** Label for the picker */
    label: string;
    /** Placeholder text */
    placeholder?: string;
    /** Whether the picker is disabled */
    disabled?: boolean;
    /** Maximum number of users that can be selected (0 = unlimited) */
    maxSelection?: number;
    /** Description text */
    description?: string;
}

// Styles
const userCardStyles = mergeStyles({
    padding: '8px 12px',
    borderRadius: '4px',
    backgroundColor: '#f3f2f1',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ':hover': {
        backgroundColor: '#edebe9',
    },
});

const searchResultStyles = mergeStyles({
    padding: '8px 12px',
    cursor: 'pointer',
    ':hover': {
        backgroundColor: '#f3f2f1',
    },
});

const selectedUserStyles = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    backgroundColor: '#e1dfdd',
    borderRadius: '16px',
    marginRight: '4px',
    marginBottom: '4px',
});

/**
 * Debounce hook for search input
 */
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

/**
 * Azure Entra User Picker Component
 */
export const EntraUserPicker: React.FC<EntraUserPickerProps> = ({
    selectedEmails,
    onSelectionChange,
    label,
    placeholder = 'Search for users...',
    disabled = false,
    maxSelection = 0,
    description,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<EntraUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<EntraUser[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [entraConfigured, setEntraConfigured] = useState<boolean | null>(null);
    const [hasUserPermissions, setHasUserPermissions] = useState<boolean | null>(null);
    const [permissionHint, setPermissionHint] = useState<string | null>(null);

    const searchBoxId = useId('search-box');
    const debouncedQuery = useDebounce(searchQuery, 300);

    // Check if Entra is configured
    useEffect(() => {
        const checkEntraStatus = async () => {
            try {
                const response = await authenticatedFetch('/api/admin/entra/status');
                if (response.ok) {
                    const data = await response.json();
                    setEntraConfigured(data.configured && data.authenticated);
                    setHasUserPermissions(data.hasUserPermissions ?? false);
                    setPermissionHint(data.permissionHint || null);
                }
            } catch (e) {
                setEntraConfigured(false);
                setHasUserPermissions(false);
            }
        };
        checkEntraStatus();
    }, []);

    // Load existing users on mount
    useEffect(() => {
        const loadExistingUsers = async () => {
            if (selectedEmails.length === 0) {
                setSelectedUsers([]);
                return;
            }

            try {
                const response = await authenticatedFetch('/api/admin/entra/users/lookup', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ emails: selectedEmails }),
                });

                if (response.ok) {
                    const data = await response.json();
                    // Build a map of original emails to found users
                    // Important: preserve the ORIGINAL email for removal, not the Entra-resolved one
                    const usersWithOriginalEmails = data.users.map((user: EntraUser) => {
                        // Find the original email that matched this user (case-insensitive)
                        const originalEmail = selectedEmails.find(
                            (e) => e.toLowerCase() === user.email?.toLowerCase() || 
                                   e.toLowerCase() === user.userPrincipalName?.toLowerCase()
                        ) || user.email;
                        return {
                            ...user,
                            originalEmail: originalEmail, // Store original for removal
                        };
                    });
                    const notFoundWithOriginal = data.notFound.map((user: EntraUser) => ({
                        ...user,
                        originalEmail: user.email, // Already the original
                    }));
                    setSelectedUsers([...usersWithOriginalEmails, ...notFoundWithOriginal]);
                } else {
                    // Fallback: create user objects from emails
                    setSelectedUsers(
                        selectedEmails.map((email) => ({
                            id: email,
                            displayName: email.split('@')[0],
                            email: email,
                            userPrincipalName: email,
                            originalEmail: email,
                            notInEntra: true,
                        }))
                    );
                }
            } catch (e) {
                // Fallback: create user objects from emails
                setSelectedUsers(
                    selectedEmails.map((email) => ({
                        id: email,
                        displayName: email.split('@')[0],
                        email: email,
                        userPrincipalName: email,
                        originalEmail: email,
                        notInEntra: true,
                    }))
                );
            }
        };

        loadExistingUsers();
    }, [selectedEmails]);

    // Search users when query changes (only if we have permissions)
    useEffect(() => {
        const searchUsers = async () => {
            // Skip search if we know we don't have permissions
            if (!hasUserPermissions) {
                setSearchResults([]);
                return;
            }
            
            if (debouncedQuery.length < 2) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            setError(null);

            try {
                const response = await authenticatedFetch(
                    `/api/admin/entra/users/search?q=${encodeURIComponent(debouncedQuery)}&limit=10`
                );

                if (response.ok) {
                    const data = await response.json();
                    // Filter out already selected users
                    const filteredResults = data.users.filter(
                        (user: EntraUser) =>
                            !selectedEmails.includes(user.email) &&
                            !selectedEmails.includes(user.userPrincipalName)
                    );
                    setSearchResults(filteredResults);
                    setIsCalloutVisible(filteredResults.length > 0);
                } else if (response.status === 403) {
                    // Permission denied - update state and don't show error
                    setHasUserPermissions(false);
                    setSearchResults([]);
                } else {
                    setError('Failed to search users');
                }
            } catch (e) {
                setError('Error searching users. Check if Azure Entra is configured.');
            } finally {
                setIsSearching(false);
            }
        };

        searchUsers();
    }, [debouncedQuery, selectedEmails, hasUserPermissions]);

    const handleAddUser = useCallback(
        (user: EntraUser) => {
            if (maxSelection > 0 && selectedEmails.length >= maxSelection) {
                setError(`Maximum ${maxSelection} users allowed`);
                return;
            }

            // Use userPrincipalName (UPN) for storage - this matches how users are identified at login
            const userIdentifier = user.userPrincipalName || user.email;
            const newEmails = [...selectedEmails, userIdentifier];
            onSelectionChange(newEmails);
            setSearchQuery('');
            setSearchResults([]);
            setIsCalloutVisible(false);
        },
        [selectedEmails, onSelectionChange, maxSelection]
    );

    const handleRemoveUser = useCallback(
        (email: string) => {
            const newEmails = selectedEmails.filter((e) => e !== email);
            onSelectionChange(newEmails);
        },
        [selectedEmails, onSelectionChange]
    );

    const handleManualAdd = useCallback(() => {
        const email = searchQuery.trim();
        if (email && email.includes('@')) {
            handleAddUser({
                id: email,
                displayName: email.split('@')[0],
                email: email,
                userPrincipalName: email,
                notInEntra: true,
            });
        }
    }, [searchQuery, handleAddUser]);

    const renderSelectedUser = (user: EntraUser) => {
        // Use originalEmail for removal (to match the stored email), fallback to email for display
        const emailForRemoval = user.originalEmail || user.email;
        return (
            <div key={user.originalEmail || user.email} className={selectedUserStyles}>
                <Persona
                    text={user.displayName}
                    secondaryText={user.email}
                    size={PersonaSize.size24}
                    presence={user.notInEntra ? PersonaPresence.none : PersonaPresence.online}
                    showSecondaryText={false}
                />
                <Text variant="small" style={{ color: '#605e5c' }}>
                    {user.email}
                </Text>
                {!disabled && (
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        title="Remove"
                        ariaLabel="Remove user"
                        styles={{ root: { height: 20, width: 20 } }}
                        onClick={() => handleRemoveUser(emailForRemoval)}
                    />
                )}
            </div>
        );
    };

    const renderSearchResult = (user: EntraUser) => (
        <div
            key={user.id}
            className={searchResultStyles}
            onClick={() => handleAddUser(user)}
            role="option"
            aria-selected={false}
        >
            <Persona
                text={user.displayName}
                secondaryText={user.email}
                tertiaryText={user.jobTitle ? `${user.jobTitle}${user.department ? ` • ${user.department}` : ''}` : user.department}
                size={PersonaSize.size40}
                presence={PersonaPresence.none}
                showSecondaryText
            />
        </div>
    );

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <Label>{label}</Label>
            {description && (
                <Text variant="small" style={{ color: '#605e5c' }}>
                    {description}
                </Text>
            )}

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
                    {selectedUsers.map(renderSelectedUser)}
                </div>
            )}

            {/* Search Box */}
            {!disabled && (
                <div style={{ position: 'relative' }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                        <Stack.Item grow>
                            <TextField
                                id={searchBoxId}
                                placeholder={placeholder}
                                value={searchQuery}
                                onChange={(_, newValue) => setSearchQuery(newValue || '')}
                                onFocus={() => searchResults.length > 0 && setIsCalloutVisible(true)}
                                disabled={disabled}
                                iconProps={{ iconName: 'Search' }}
                                styles={{ root: { width: '100%' } }}
                            />
                        </Stack.Item>
                        {searchQuery.includes('@') && (
                            <DefaultButton
                                text="Add manually"
                                onClick={handleManualAdd}
                                iconProps={{ iconName: 'Add' }}
                            />
                        )}
                    </Stack>

                    {isSearching && (
                        <Spinner
                            size={SpinnerSize.small}
                            style={{ position: 'absolute', right: 140, top: 10 }}
                        />
                    )}

                    {/* Search Results Callout */}
                    {isCalloutVisible && searchResults.length > 0 && (
                        <Callout
                            target={`#${searchBoxId}`}
                            onDismiss={() => setIsCalloutVisible(false)}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            isBeakVisible={false}
                            styles={{
                                root: { width: 400, maxHeight: 300, overflowY: 'auto' },
                            }}
                        >
                            {searchResults.map(renderSearchResult)}
                        </Callout>
                    )}
                </div>
            )}

            {/* Status Messages */}
            {entraConfigured === false && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Azure Entra search is not configured. You can still add users manually by typing their email.
                </MessageBar>
            )}

            {entraConfigured && hasUserPermissions === false && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text>Azure Entra user search requires Graph API permissions. You can still add users manually by typing their email.</Text>
                        {permissionHint && (
                            <Text variant="small" style={{ fontStyle: 'italic' }}>
                                💡 {permissionHint}
                            </Text>
                        )}
                    </Stack>
                </MessageBar>
            )}

            {error && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    onDismiss={() => setError(null)}
                >
                    {error}
                </MessageBar>
            )}

            {/* Help Text */}
            {!disabled && entraConfigured && hasUserPermissions && (
                <Text variant="small" style={{ color: '#605e5c', fontStyle: 'italic' }}>
                    💡 Start typing to search Azure Entra users, or enter an email manually
                </Text>
            )}

            {!disabled && (!entraConfigured || !hasUserPermissions) && (
                <Text variant="small" style={{ color: '#605e5c', fontStyle: 'italic' }}>
                    💡 Enter email addresses manually (e.g., user@company.com) and click "Add manually"
                </Text>
            )}
        </Stack>
    );
};

/**
 * Azure Entra Group Picker Component
 */
interface EntraGroupPickerProps {
    selectedGroupIds: string[];
    onSelectionChange: (groupIds: string[]) => void;
    label: string;
    placeholder?: string;
    disabled?: boolean;
    description?: string;
}

interface EntraGroup {
    id: string;
    displayName: string;
    description?: string;
    mail?: string;
}

export const EntraGroupPicker: React.FC<EntraGroupPickerProps> = ({
    selectedGroupIds,
    onSelectionChange,
    label,
    placeholder = 'Search for groups...',
    disabled = false,
    description,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<EntraGroup[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<EntraGroup[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasGroupPermissions, setHasGroupPermissions] = useState<boolean | null>(null);

    const searchBoxId = useId('group-search-box');
    const debouncedQuery = useDebounce(searchQuery, 300);

    // Check permissions on mount
    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const response = await authenticatedFetch('/api/admin/entra/status');
                if (response.ok) {
                    const data = await response.json();
                    // Use hasUserPermissions as proxy - typically same permission covers groups
                    setHasGroupPermissions(data.hasUserPermissions ?? false);
                }
            } catch (e) {
                setHasGroupPermissions(false);
            }
        };
        checkPermissions();
    }, []);

    // Initialize selected groups from IDs
    useEffect(() => {
        setSelectedGroups(
            selectedGroupIds.map((id) => ({
                id,
                displayName: id, // Will be updated if we can look it up
            }))
        );
    }, [selectedGroupIds]);

    // Search groups (only if we have permissions)
    useEffect(() => {
        const searchGroups = async () => {
            // Skip if no permissions
            if (!hasGroupPermissions) {
                setSearchResults([]);
                return;
            }
            
            if (debouncedQuery.length < 2) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            setError(null);

            try {
                const response = await authenticatedFetch(
                    `/api/admin/entra/groups/search?q=${encodeURIComponent(debouncedQuery)}&limit=10`
                );

                if (response.ok) {
                    const data = await response.json();
                    const filteredResults = data.groups.filter(
                        (group: EntraGroup) => !selectedGroupIds.includes(group.id)
                    );
                    setSearchResults(filteredResults);
                    setIsCalloutVisible(filteredResults.length > 0);
                } else if (response.status === 403) {
                    setHasGroupPermissions(false);
                    setSearchResults([]);
                }
            } catch (e) {
                setError('Error searching groups');
            } finally {
                setIsSearching(false);
            }
        };

        searchGroups();
    }, [debouncedQuery, selectedGroupIds, hasGroupPermissions]);

    const handleAddGroup = useCallback(
        (group: EntraGroup) => {
            const newIds = [...selectedGroupIds, group.id];
            onSelectionChange(newIds);
            setSearchQuery('');
            setSearchResults([]);
            setIsCalloutVisible(false);
        },
        [selectedGroupIds, onSelectionChange]
    );

    const handleRemoveGroup = useCallback(
        (groupId: string) => {
            const newIds = selectedGroupIds.filter((id) => id !== groupId);
            onSelectionChange(newIds);
        },
        [selectedGroupIds, onSelectionChange]
    );

    const handleManualAdd = useCallback(() => {
        const groupName = searchQuery.trim();
        if (groupName) {
            handleAddGroup({
                id: groupName,
                displayName: groupName,
            });
        }
    }, [searchQuery, handleAddGroup]);

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <Label>{label}</Label>
            {description && (
                <Text variant="small" style={{ color: '#605e5c' }}>
                    {description}
                </Text>
            )}

            {/* Selected Groups */}
            {selectedGroups.length > 0 && (
                <Stack tokens={{ childrenGap: 4 }}>
                    {selectedGroups.map((group) => (
                        <div key={group.id} className={userCardStyles}>
                            <Stack>
                                <Text variant="medium" style={{ fontWeight: 600 }}>
                                    {group.displayName}
                                </Text>
                                <Text variant="small" style={{ color: '#605e5c' }}>
                                    {group.id}
                                </Text>
                            </Stack>
                            {!disabled && (
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title="Remove"
                                    onClick={() => handleRemoveGroup(group.id)}
                                />
                            )}
                        </div>
                    ))}
                </Stack>
            )}

            {/* Search Box */}
            {!disabled && (
                <div style={{ position: 'relative' }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                        <Stack.Item grow>
                            <TextField
                                id={searchBoxId}
                                placeholder={placeholder}
                                value={searchQuery}
                                onChange={(_, newValue) => setSearchQuery(newValue || '')}
                                onFocus={() => searchResults.length > 0 && setIsCalloutVisible(true)}
                                iconProps={{ iconName: 'Search' }}
                            />
                        </Stack.Item>
                        {searchQuery.length > 0 && (
                            <DefaultButton
                                text="Add manually"
                                onClick={handleManualAdd}
                                iconProps={{ iconName: 'Add' }}
                            />
                        )}
                    </Stack>

                    {isSearching && (
                        <Spinner
                            size={SpinnerSize.small}
                            style={{ position: 'absolute', right: 140, top: 10 }}
                        />
                    )}

                    {isCalloutVisible && searchResults.length > 0 && (
                        <Callout
                            target={`#${searchBoxId}`}
                            onDismiss={() => setIsCalloutVisible(false)}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            isBeakVisible={false}
                            styles={{ root: { width: 400, maxHeight: 300, overflowY: 'auto' } }}
                        >
                            {searchResults.map((group) => (
                                <div
                                    key={group.id}
                                    className={searchResultStyles}
                                    onClick={() => handleAddGroup(group)}
                                >
                                    <Stack>
                                        <Text variant="medium" style={{ fontWeight: 600 }}>
                                            {group.displayName}
                                        </Text>
                                        {group.description && (
                                            <Text variant="small" style={{ color: '#605e5c' }}>
                                                {group.description}
                                            </Text>
                                        )}
                                    </Stack>
                                </div>
                            ))}
                        </Callout>
                    )}
                </div>
            )}

            {hasGroupPermissions === false && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text>Azure Entra group search requires Graph API permissions. Enter group names manually.</Text>
                        <Text variant="small" style={{ fontStyle: 'italic' }}>
                            💡 Add Group.Read.All permission to your Azure AD app registration and grant admin consent.
                        </Text>
                    </Stack>
                </MessageBar>
            )}

            {error && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
                    {error}
                </MessageBar>
            )}

            {!disabled && (
                <Text variant="small" style={{ color: '#605e5c', fontStyle: 'italic' }}>
                    💡 {hasGroupPermissions ? 'Start typing to search Azure Entra groups' : 'Enter group names manually and click "Add manually"'}
                </Text>
            )}
        </Stack>
    );
};

export default EntraUserPicker;
